import { OrganizationMembership, useUserStore } from '@/store/user.ts'
import PropertyPopoverTrigger from '@/components/common/propertyPopoverOverlay/PropertyPopoverTrigger.tsx'
import OrgAvatar from '@/components/common/OrgAvatar.tsx'
import { PopoverProperty } from '@/components/common/propertyPopoverOverlay/Property.tsx'
import { useTranslation } from 'react-i18next'
import PropertyPopover from '@/components/common/propertyPopoverOverlay/PropertyPopover.tsx'
import { useSwitchOrganization } from '@/lib/hooks/useSwitchOrganization.ts'

type MembershipWithIri = OrganizationMembership & { '@id'?: string }

export default function NavOrgsOverlay() {
  const { t } = useTranslation()
  const user = useUserStore(state => state.user)
  const currOrg = useUserStore(state => state.currentOrganizationMembership)?.organization
  const { switchTo, isSwitching } = useSwitchOrganization()

  const memberships = (user?.organizationMemberships ?? []) as MembershipWithIri[]
  const selectable = memberships.filter(membership => membership['@id'] && membership.organization)

  // With a single membership there is nothing to switch to, so keep the list empty and let
  // `fallbackMessage` explain that — same convention as the stage picker's singleProjectWarning.
  const properties: PopoverProperty[] =
    selectable.length > 1
      ? selectable.map(membership => ({
          label: membership.organization?.name ?? '',
          value: membership['@id'] as string,
          icon: <OrgAvatar logosrc={membership.organization?.icon?.contentUrlSmall} />,
          disabled: isSwitching,
        }))
      : []

  const activeMembershipIri = user?.activeMembership
  const selected = activeMembershipIri ? [activeMembershipIri] : []

  // Use PropertyPopover directly (not PopoverOverlay) so the org picker
  // opens anchored above the trigger on every screen size. PopoverOverlay
  // would render as a bottom-sheet on mobile — disorienting when the
  // trigger lives at the very bottom of the drawer (the sheet would
  // occlude the button you just tapped).
  return (
    <PropertyPopover
      className={`md:max-w-[calc(var(--sidebar-width)-32px)]`}
      properties={properties}
      fallbackMessage={t(`navigation.singleOrgWarning`)}
      addSelectedValue={value => {
        void switchTo(value)
      }}
      closeOnSelect={true}
      selectedValues={selected}
      popoverHeader={t('navigation.organizationsSearchLabel')}
      customTrigger={
        <PropertyPopoverTrigger
          className={`w-full justify-start`}
          variant={`outline`}
          label={currOrg?.name}
          icon={<OrgAvatar logosrc={currOrg?.icon?.contentUrlSmall} />}
        />
      }
    />
  )
}
